import * as React from 'react';
import { FormGroup, FormControl } from 'react-bootstrap';
import { components } from 'react-select';
import type {
	FormatOptionLabelMeta,
	StylesConfig,
	PropsValue,
	OnChangeValue,
	InputActionMeta,
	ActionMeta,
	Theme,
	GroupBase,
	ClearIndicatorProps,
	ControlProps,
	MenuProps,
	OptionsOrGroups,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';
import type { WrappedFieldProps } from 'redux-form';

import { bemBlock } from 'af-utils/bem.util';

import Label from 'af-components/LockedValue/Label';
import type { OwnProps as TooltipProps } from 'af-components/Tooltip';

type DefaultOptionType = {
	label: string;
	value: number | string;
};

interface OwnProps<OptionType = DefaultOptionType> {
	addonComponent?: () => React.JSX.Element | string;
	allowNew?: boolean;
	className?: string;
	containerClassName?: string;
	containerId?: string;
	disableErrorMessage?: boolean;
	filterOption?: (option: { label: string; value: string; data: OptionType; }, rawInput: string) => boolean;
	fixed?: boolean;
	formatCreateLabel?: (input: string) => React.ReactNode;
	formatOptionLabel?: (option: OptionType, labelMeta: FormatOptionLabelMeta<OptionType>) => React.ReactNode;
	formatSelectedOption?: (option: OptionType) => React.ReactNode;
	getNewOptionData?: (inputValue: string, optionLabel: string) => OptionType;
	getOptionLabel?: (option: OptionType) => string;
	getOptionValue?: (option: OptionType) => string;
	isDisabled?: boolean;
	isValidNewOption?: (inputValue: string, value: OptionType, options: OptionType[]) => boolean;
	label?: string | React.JSX.Element;
	onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
	onClear?: (selectedOption: OnChangeValue<OptionType, false>) => Promise<void> | void;
	onCreateNew?: (inputValue: string) => void;
	onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
	onLazyLoad?: (isLazyLoaded: boolean) => Promise<void>;
	onValueChange?: (selectedOption: OnChangeValue<OptionType, false>) => Promise<void>;
	options: OptionType[];
	placeholder?: string;
	tooltipMessage?: TooltipProps['message'];
	valueKey?: string;
	onInputChange?: ((newValue: string, actionMeta: InputActionMeta) => void);
	inputValue?: string;
	errorKey?: string;
	noOptionsMessage?: (obj: { inputValue: string; }) => string | null;
	onMenuScrollToBottom?: (event: WheelEvent | TouchEvent) => void;
}

type Props<OptionType> = OwnProps<OptionType> & WrappedFieldProps;

interface CustomThemeColors {
	primary25: string;
	primary50: string;
	primary75: string;
}

const getTheme = (theme: Theme): Theme => ({
	...theme,
	borderRadius: 2,
	colors: {
		...theme.colors,
		...({
			primary25: '#FEF4E9',
			primary50: '#FEF4E9',
			primary75: '#FFA726',
		} as typeof theme.colors & CustomThemeColors),
		primary: '#FFA726',
	},
});

const fixedMenuStyle: React.CSSProperties = {
	left: 'initial',
	right: 'initial',
	top: 'initial',
	bottom: 'initial',
};

const customStylesBase: StylesConfig = {
	control: (base) => ({
		...base,
		boxShadow: '0 !important',
		'&:hover': { borderColor: '#FFA726' },
	}),
};

// Made getComponents fully generic to inherit OptionType structural constraints safely
const getComponents = <OptionType,>(
	inputRef: React.RefObject<HTMLInputElement | null>,
	fixed: boolean
) => {
	return {
		IndicatorSeparator: null,
		DropdownIndicator: () => <span className="icon-down" />,
		ClearIndicator: (props: ClearIndicatorProps<OptionType, false, GroupBase<OptionType>>) => (
			<div {...props.innerProps}>
				<span className="icon-close" />
			</div>
		),
		Control: (props: ControlProps<OptionType, false, GroupBase<OptionType>>) => (
			<div ref={inputRef}>
				<components.Control {...props} />
			</div>
		),
		Menu: (props: MenuProps<OptionType, false, GroupBase<OptionType>>) => {
			const dropdownMenuClassName = bemBlock('react-select-dropdown', {
				'scroll-container': true,
				'full-width': true,
				fixed: fixed,
			});
			return <components.Menu {...props} className={dropdownMenuClassName} />;
		},
	};
};

const defaultFormatCreateLabel = (input: string) => `Create: ${input}`;

const getContainerClassName = (containerClassName: string | undefined, addonComponent: (() => React.JSX.Element | string) | undefined) => {
	const containerClass = containerClassName ? containerClassName : '';
	const addonClass = addonComponent ? 'react-select-container--with-addon' : '';
	return `react-select-container ${containerClass} ${addonClass}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getSelectClassName = (additionalClassName: string | undefined, touched: boolean, error: any) => {
	const className = additionalClassName ? additionalClassName : '';
	const errorClass = (touched && error) ? 'react-select-field--error' : '';
	return `react-select-field ${className} ${errorClass}`;
};

const generateQAId = (propName: string) => `accqa__${propName.replaceAll('[', '-').replaceAll('.', '__').replaceAll(']', '').replaceAll(' ', '_')}`;

const SelectField = <OptionType = DefaultOptionType,>(props: Props<OptionType>) => {
	const {
		addonComponent,
		allowNew = false,
		className,
		containerClassName,
		containerId,
		disableErrorMessage = false,
		filterOption,
		fixed = false,
		formatCreateLabel,
		formatOptionLabel,
		formatSelectedOption,
		getNewOptionData,
		getOptionLabel,
		getOptionValue,
		input,
		isDisabled = false,
		isValidNewOption,
		label,
		meta: { error, warning, touched },
		onBlur,
		onClear,
		onCreateNew,
		onFocus,
		onLazyLoad,
		onValueChange,
		options = [],
		placeholder,
		tooltipMessage,
		valueKey,
		onInputChange,
		inputValue,
		errorKey,
		noOptionsMessage,
		onMenuScrollToBottom,
	} = props;
	const { value } = input;

	const inputRef = React.useRef<HTMLInputElement>(null);
	const [isLazyLoading, setIsLazyLoading] = React.useState(false);
	const [menuStyle, setMenuStyle] = React.useState<React.CSSProperties>(fixedMenuStyle);

	const updateMenuPosition = React.useCallback(() => {
		const {
			x: inputPositionX = 0,
			bottom: inputBottom = 0,
			width: inputWidth = 0,
		} = inputRef.current?.getClientRects()?.[0] ?? {};

		setMenuStyle({ ...menuStyle, top: inputBottom, left: inputPositionX, width: inputWidth, bottom: 'auto' });
	}, [menuStyle]);

	React.useEffect(() => {
		if (isLazyLoading) {
			const isLazyLoaded = !!options?.length;
			onLazyLoad?.(isLazyLoaded);
			setIsLazyLoading(false);
		}
	}, [isLazyLoading, onLazyLoad, options]);

	React.useEffect(() => {
		if (fixed) window.addEventListener('resize', updateMenuPosition);
		return () => {
			if (fixed) window.removeEventListener('resize', updateMenuPosition);
		};
	}, [updateMenuPosition, fixed]);

	const customStyles = React.useMemo(() => {
		if (!fixed) return customStylesBase;
		return {
			...customStylesBase,
			menu: (base) => ({ ...base, ...menuStyle }),
		};
	}, [fixed, menuStyle]);

	const onChange = React.useCallback((item: OnChangeValue<OptionType, false>, { action }: ActionMeta<OptionType>) => {
		const selected = valueKey && item ? item[valueKey] : item;
		input.onChange(selected);
		if (action === 'clear' && onClear) {
			onClear(item);
		} else if (action === 'select-option' && onValueChange) {
			onValueChange(item);
		}
	}, [valueKey, input, onClear, onValueChange]);

	const handleBlur = React.useCallback((event: React.FocusEvent<HTMLElement>) => {
		input.onBlur(undefined);
		onBlur?.(event);
	}, [onBlur, input]);

	const handleFocus = React.useCallback((event: React.FocusEvent<HTMLElement>) => {
		input.onFocus(event);
		onFocus?.(event);
		if (onLazyLoad && !isLazyLoading) {
			setIsLazyLoading(true);
		}
		if (fixed) {
			updateMenuPosition();
		}
	}, [onFocus, onLazyLoad, fixed, isLazyLoading, updateMenuPosition, input]);

	const handleFormatOptionLabel = React.useCallback((
		option: OptionType & { __isNew__?: boolean; },
		labelMeta: FormatOptionLabelMeta<OptionType>
	) => {
		if (option.__isNew__) {
			if (!allowNew) return <div>No options</div>;
			const formatCreate = formatCreateLabel ?? defaultFormatCreateLabel;
			return <span>{formatCreate(labelMeta.inputValue)}</span>;
		}
		if (formatSelectedOption && !!Object.keys(labelMeta.selectValue ?? {}).length) {
			return formatSelectedOption(option);
		}
		if (formatOptionLabel) return formatOptionLabel(option, labelMeta);
		if (getOptionLabel) return <div>{getOptionLabel(option)}</div>;
		return null;
	}, [allowNew, formatOptionLabel, getOptionLabel, formatCreateLabel, formatSelectedOption]);

	const creatableProps = React.useMemo(() => {
		return allowNew
			? {
				onCreateOption: onCreateNew,
				getNewOptionData,
				isValidNewOption: isValidNewOption
					? (
						_inputValue: string,
						_value: PropsValue<OptionType>,
						_options: OptionsOrGroups<OptionType, GroupBase<OptionType>>
					) => {
						// 1. Safely parse single value out of PropsValue array/item/null
						const singleValue = Array.isArray(_value) ? _value[0] : _value;

						// 2. Safely filter out grouped option objects to get a flat array
						const flatOptions = _options.filter(
							(o): o is OptionType => !o || !('options' in (o as OptionType[]))
						) as OptionType[];

						return isValidNewOption(_inputValue, singleValue as OptionType, flatOptions);
					}
					: undefined,
			}
			: {};
	}, [allowNew, onCreateNew, isValidNewOption, getNewOptionData]);

	const newError = React.useMemo(() => {
		if (!errorKey) return error;
		return error && Object.keys(error).length ? error[errorKey] : null;
	}, [error, errorKey]);

	const containerClass = getContainerClassName(containerClassName, addonComponent);
	const selectClass = getSelectClassName(className, touched, newError);
	const memoizedComponents = React.useMemo(() => getComponents<OptionType>(inputRef, fixed), [fixed]);

	// Value computed cleanly as PropsValue matching exact underlying state structure
	const newValue = valueKey && value
		? { [valueKey]: value, label: value }
		: (value === '' ? null : value);

	const generatedQAId = generateQAId(placeholder ?? className ?? '');

	return (
		<FormGroup className={containerClass} id={generatedQAId}>
			{label && <Label label={label} tooltipMessage={tooltipMessage} withMargin={true} />}
			<CreatableSelect<OptionType, false, GroupBase<OptionType>>
				{...creatableProps}
				className={selectClass}
				components={memoizedComponents}
				filterOption={filterOption}
				formatOptionLabel={handleFormatOptionLabel}
				getOptionLabel={getOptionLabel}
				getOptionValue={getOptionValue}
				id={containerId}
				inputValue={inputValue ?? undefined}
				isClearable={!isDisabled}
				isDisabled={isDisabled}
				isLoading={isLazyLoading}
				isSearchable={!isDisabled}
				noOptionsMessage={noOptionsMessage}
				onBlur={handleBlur}
				onChange={onChange}
				onFocus={handleFocus}
				onInputChange={onInputChange}
				onMenuScrollToBottom={onMenuScrollToBottom}
				options={options}
				placeholder={placeholder}
				styles={customStyles}
				theme={getTheme}
				value={newValue}
			/>
			{addonComponent?.()}
			<FormControl.Feedback />
			{(touched && !disableErrorMessage) &&
				((newError && <span className="help-block"><span className="icon-info" /> {newError}</span>) ||
					(warning && <span className="help-block text-orange"><span className="icon-info" /> {warning}</span>))
			}
		</FormGroup>
	);
};

export default SelectField;
